import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Pencil, Archive, ArchiveRestore, ExternalLink, Plus, Check, X, Clock } from "lucide-react";
import { DataTable } from "@/components/ui/data-table/index";
import { Column } from "@/components/ui/data-table/types";
import { Company } from "@/types/company/company.types";
import { useLanguage } from "../contexts/LanguageContext";
import { useData } from "@/hooks/useData";
import { useCompanyFilters } from "@/hooks/companies/useCompanyFilters";
import { useCompanyActions } from "@/hooks/companies/useCompanyActions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TableHeader } from "@/components/ui/table-header/index";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { Badge } from "@/components/ui/badge";
import { FootprintDocument, FootprintProduct } from "@/types/results";

type CompanyRow = Company & {
  productTypes?: string[];
  rockTypes?: string[];
};

export const Companies = () => {
  const { user, claims, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const {
    data: companies,
    loading: companiesLoading,
    error,
  } = useData<Company>({
    collectionName: "companies",
  });
  // Load footprints and products to derive stone/product types per company
  const { data: footprintDocuments } = useData<FootprintDocument>({ collectionName: "footprints" });
  const { data: footprintProducts } = useData<FootprintProduct>({ collectionName: "footprint_products" });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [registrationTab, setRegistrationTab] = useState("all"); // all, registered, or notRegistered
  const [companyFilters, setCompanyFilters] = useState<string[]>([]);
  const [activityTypeFilters, setActivityTypeFilters] = useState<string[]>([]);
  const [rockTypeFilters, setRockTypeFilters] = useState<string[]>([]);
  const [productTypeFilters, setProductTypeFilters] = useState<string[]>([]);

  // Convert tab states to filter states
  const showOnlyRegistered = registrationTab === "registered";
  const showOnlyNotRegistered = registrationTab === "notRegistered";

  // Map footprint_id to company_id for quick joins
  const footprintCompanyMap = useMemo(() => {
    const map: Record<string, string> = {};
    (footprintDocuments || []).forEach((fp) => {
      if (fp.id && fp.company_id) {
        map[fp.id] = fp.company_id;
      }
    });
    return map;
  }, [footprintDocuments]);

  // Aggregate product and stone types per company
  const companyTypes = useMemo(() => {
    const agg: Record<string, { productTypes: Set<string>; rockTypes: Set<string> }> = {};
    (footprintProducts || []).forEach((prod) => {
      const companyId = footprintCompanyMap[prod.footprint_id];
      if (!companyId) return;
      if (!agg[companyId]) {
        agg[companyId] = { productTypes: new Set(), rockTypes: new Set() };
      }
      if (prod.product_type) {
        agg[companyId].productTypes.add(prod.product_type);
      }
      if (prod.stone_type_name) {
        agg[companyId].rockTypes.add(prod.stone_type_name);
      }
    });
    return agg;
  }, [footprintProducts, footprintCompanyMap]);

  // Enrich companies with aggregated tags for display and simple sorting/filtering
  const companiesWithTypes: CompanyRow[] = useMemo(() => {
    return (companies || []).map((c) => {
      const types = companyTypes[c.id];
      const productTypes = types ? Array.from(types.productTypes) : [];
      const rockTypes = types ? Array.from(types.rockTypes) : [];
      return {
        ...c,
        productType: productTypes.join(", "),
        rockType: rockTypes.join(", "),
        productTypes,
        rockTypes,
      } as CompanyRow;
    });
  }, [companies, companyTypes]);

  const { filteredCompanies, getUniqueOptions } = useCompanyFilters({
    companies: companiesWithTypes,
    searchTerm,
    showArchived,
    showOnlyRegistered,
    showOnlyNotRegistered,
    companyFilters,
    activityTypeFilters,
    rockTypeFilters,
    productTypeFilters,
  });

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    closeDialog,
    handleConfirm,
    handleArchive,
    handleRestore,
  } = useCompanyActions();

  useEffect(() => {
    if (!authLoading && !companiesLoading) {
      setIsLoading(false);

      if (!user || !claims || (claims.role !== "assimagra" && claims.role !== "get2c")) {
        navigate("/");
        return;
      }
    }
  }, [user, claims, authLoading, companiesLoading, navigate]);

  useEffect(() => {
    if (error) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  const renderTagList = (items: string[]) => {
    const unique = Array.from(new Set(items.filter(Boolean)));
    if (unique.length === 0) return <span className="truncate block">-</span>;
    const visible = unique.slice(0, 2);
    const extra = unique.length - visible.length;
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {visible.map((label) => (
          <Badge key={label} variant="secondary" className="whitespace-nowrap">
            {label}
          </Badge>
        ))}
        {extra > 0 && (
          <Badge variant="secondary" className="whitespace-nowrap">{`+${extra}`}</Badge>
        )}
      </div>
    );
  };

  const columns: Column<CompanyRow>[] = [
    {
      header: t("companies.table.company"),
      accessorKey: "name",
      sortable: true,
      className: "w-[18%] min-w-[300px]",
      cell: (value: string, row: CompanyRow) => (
        <div className="flex items-center gap-2">
          <span className="truncate flex-1" title={value}>
            {value}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/companies/${row.id}/dashboard`)}
                  className="h-7 w-7 p-0 hover:bg-gray-100 flex-shrink-0"
                >
                  <ExternalLink className="h-3.5 w-3.5 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <p>{t("companies.actions.visit")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      header: t("companies.table.associateNumber"),
      accessorKey: "associateNumber",
      sortable: true,
      className: "w-[8%] min-w-[120px] text-center",
      cell: (_value: string, row: CompanyRow) => <span className="font-mono text-sm">{row.associateNumber || "-"}</span>,
    },
    {
      header: t("companies.table.basinName"),
      accessorKey: "basinName",
      sortable: true,
      className: "w-[11%] min-w-[180px]",
      cell: (_value: string, row: CompanyRow) => {
        const displayValue = row.basinName && row.basinName !== "undefined" ? row.basinName : "-";
        return (
          <span className="text-sm truncate block" title={displayValue}>
            {displayValue}
          </span>
        );
      },
    },
    {
      header: t("companies.table.weatherStationName"),
      accessorKey: "weatherStationName",
      sortable: true,
      className: "w-[12%] min-w-[200px]",
      cell: (_value: string, row: CompanyRow) => {
        const displayValue =
          row.weatherStationName && row.weatherStationName !== "undefined" ? row.weatherStationName : "-";
        return (
          <span className="text-sm truncate block" title={displayValue}>
            {displayValue}
          </span>
        );
      },
    },
    {
      header: t("companies.table.activityType"),
      accessorKey: "activityType",
      sortable: true,
      className: "w-[10%] min-w-[180px] md:table-cell hidden",
      headerClassName: "md:table-cell hidden",
      cell: (value: string) => {
        if (!value || value === "undefined") {
          return (
            <div className="truncate" title="-">
              -
            </div>
          );
        }
        const translatedValue = t(`activityTypes.${value.toLowerCase().replace(/ /g, "_")}`);
        return (
          <div className="truncate" title={translatedValue}>
            {translatedValue}
          </div>
        );
      },
    },
    {
      header: t("companies.table.stoneType"),
      accessorKey: "rockType",
      sortable: true,
      className: "w-[12%] min-w-[200px] md:table-cell hidden",
      headerClassName: "md:table-cell hidden",
      cell: (_value: string, row: CompanyRow) => renderTagList(row.rockTypes || []),
    },
    {
      header: t("companies.table.productType"),
      accessorKey: "productType",
      sortable: true,
      className: "w-[12%] min-w-[200px] lg:table-cell hidden",
      headerClassName: "lg:table-cell hidden",
      cell: (_value: string, row: CompanyRow) => renderTagList(row.productTypes || []),
    },
    {
      header: t("companies.table.status"),
      accessorKey: "accountState",
      sortable: true,
      className: "w-[8%] min-w-[140px]",
      cell: (_value: string, row: CompanyRow) => {
        const { accountState } = row;

        if (accountState?.isRegistered) {
          return (
            <div className="flex justify-center">
              <div className="px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs flex items-center gap-1 whitespace-nowrap">
                <Check className="h-3 w-3" /> {t("companies.registered")}
              </div>
            </div>
          );
        } else if (accountState?.email && accountState.email.trim() !== "" && !accountState.isRegistered) {
          return (
            <div className="flex justify-center">
              <div className="px-2 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 text-xs flex items-center gap-1 whitespace-nowrap">
                <Clock className="h-3 w-3" /> {t("footprints.pending")}
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex justify-center">
              <div className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 border border-gray-200 text-xs flex items-center gap-1 whitespace-nowrap">
                <X className="h-3 w-3" /> {t("companies.notRegistered")}
              </div>
            </div>
          );
        }
      },
    },
    {
      header: t("companies.table.actions"),
      accessorKey: "id",
      sortable: false,
      className: "w-[5%] min-w-[120px]",
      cell: (_value: string, row: CompanyRow) => (
        <div className="flex gap-2 justify-end">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/companies/${row.id}/edit`)}
                  className="h-8 w-8 hover:bg-gray-100"
                >
                  <Pencil className="h-4 w-4 text-gray-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <p>{t("companies.actions.edit")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                {!row.isArchived ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleArchive(row.id, row.name)}
                    className="h-8 w-8 hover:bg-gray-100"
                  >
                    <Archive className="h-4 w-4 text-gray-500" />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRestore(row.id, row.name)}
                    className="h-8 w-8 hover:bg-gray-100"
                  >
                    <ArchiveRestore className="h-4 w-4 text-gray-500" />
                  </Button>
                )}
              </TooltipTrigger>
              <TooltipContent side="top" align="center">
                <p>{!row.isArchived ? t("companies.actions.archive") : t("companies.actions.restore")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  const filters = [
    {
      label: t("companies.filters.company"),
      options: getUniqueOptions("name").sort((a, b) => a.label.localeCompare(b.label)),
      selectedValues: companyFilters,
      onChange: setCompanyFilters,
      showSearch: true,
    },
    {
      label: t("companies.filters.activityType"),
      options: getUniqueOptions("activityType"),
      selectedValues: activityTypeFilters,
      onChange: setActivityTypeFilters,
    },
    {
      label: t("companies.filters.rockType"),
      options: getUniqueOptions("rockType"),
      selectedValues: rockTypeFilters,
      onChange: setRockTypeFilters,
    },
    {
      label: t("companies.filters.productType"),
      options: getUniqueOptions("productType"),
      selectedValues: productTypeFilters,
      onChange: setProductTypeFilters,
    },
  ];

  const tabGroups = [
    {
      currentValue: showArchived,
      onValueChange: setShowArchived,
      options: [
        {
          value: false,
          label: t("companies.filters.active"),
        },
        {
          value: true,
          label: t("companies.filters.archived"),
        },
      ],
    },
    {
      currentValue: registrationTab,
      onValueChange: setRegistrationTab,
      options: [
        {
          value: "all",
          label: t("companies.filters.allCompanies"),
        },
        {
          value: "registered",
          label: t("companies.filters.registered"),
        },
        {
          value: "notRegistered",
          label: t("companies.filters.notRegistered"),
        },
      ],
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-2">
        <h1 className="text-xl sm:text-2xl font-bold">{t("companies.title")}</h1>
        <Button
          onClick={() => navigate("/companies/add")}
          className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("companies.addButton")}
        </Button>
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("companies.searchPlaceholder")}
        filters={filters}
        tabGroups={tabGroups}
        onClearAll={() => {
          setCompanyFilters([]);
          setActivityTypeFilters([]);
          setRockTypeFilters([]);
          setProductTypeFilters([]);
          setSearchTerm("");
        }}
      />

      <div className="overflow-x-auto w-full">
        <DataTable
          columns={columns}
          data={filteredCompanies as unknown as CompanyRow[]}
          pageSize={10}
          onEdit={(id) => navigate(`/companies/edit/${id}`)}
          className="w-full"
        />
      </div>

      <ConfirmationDialog
        isOpen={confirmDialogOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        title={
          confirmAction?.type === "archive" ? t("companies.confirmArchive.title") : t("companies.confirmRestore.title")
        }
        description={
          confirmAction?.type === "archive"
            ? t("companies.confirmArchive.description", { name: confirmAction?.companyName || "" })
            : t("companies.confirmRestore.description", { name: confirmAction?.companyName || "" })
        }
        confirmText={
          isConfirmActionLoading
            ? t(
                confirmAction?.type === "archive"
                  ? "companies.confirmArchive.actionLoading"
                  : "companies.confirmRestore.actionLoading"
              )
            : t(
                confirmAction?.type === "archive"
                  ? "companies.confirmArchive.action"
                  : "companies.confirmRestore.action"
              )
        }
        cancelText={t("common.cancel")}
        variant={confirmAction?.type === "archive" ? "destructive" : "default"}
        isLoading={isConfirmActionLoading}
      />
    </div>
  );
};
